import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CmpMemberDashboardAccessReturn, useGetActiveCategoryAdvertisingBannersForChamberMemberQuery } from '../../../graphql/types';
import { RedirectUri } from '../../enumStrings';
import MemberInvoiceNavSummary from '../../pages/ChamberMemberAdmin/Invoices/MemberInvoiceNavSummary';
import CmpMemberHelpModal from './CmpMemberHelpModal';
import LanguageIcon from '../../../images/nav-icons/language.png';
import HelpIcon from '../../../images/nav-icons/help.png';
import LogoutIcon from '../../../images/nav-icons/logout.png';

// Wave C ACON-1 / Member Dashboard design (Accelerator ACC:L36-124): the CMP
// member sidebar. CPA-only members keep the original navigation (Nav.tsx).
const GOLD = '#E1A731';
const GRAY = '#727272';
const GREEN = '#10B981';
const BLACK = '#000000';

export type CmpMemberSidebarProps = {
  access: CmpMemberDashboardAccessReturn;
  chamberName?: string | null;
  memberName?: string | null;
  memberLogo?: string | null;
  featuredMember: boolean;
  sponsorLogo?: string | null;
  sponsorLogoLink?: string | null;
  /** Essentials (CPA Basic) chambers do not offer perks (design: locked items). */
  perksOffered?: boolean;
  helpContactName?: string | null;
  helpContactEmail?: string | null;
  onLogout: () => void;
  onNavigate?: () => void;
};

/** "Signed in as" summary from the access the backend granted. */
export const getCmpMemberRoleSummary = (access: CmpMemberDashboardAccessReturn): string => {
  if (access.isPrimaryContact) return 'Full access to all dashboard features';
  const parts = [
    access.canManageProfile ? 'Update profile' : '',
    access.canCreatePerks || access.canManagePerks ? 'Create and manage perks' : '',
    access.canManageBanners ? 'Category banners' : '',
    access.canViewInvoices ? 'View and pay invoices' : '',
  ].filter(Boolean);
  return parts.join(' · ');
};

type ContactSource = {
  adminConsoleContactName?: string | null; adminConsoleLoginEmail?: string | null;
  contactName?: string | null; email?: string | null;
  customContacts?: readonly ({ name?: string | null; email?: string | null } | null)[] | null;
} | null | undefined;

/** Name of the member contact that owns the signed-in email, for the Help form. */
export const getSignedInContactName = (member: ContactSource, email?: string | null): string => {
  const target = (email || '').trim().toLowerCase();
  if (!member || !target) return '';
  const same = (value?: string | null) => (value || '').trim().toLowerCase() === target;
  const custom = (member.customContacts || []).find(contact => contact && same(contact.email) && (contact.name || '').trim());
  if (custom) return (custom.name || '').trim();
  if (same(member.adminConsoleLoginEmail) && (member.adminConsoleContactName || '').trim()) return (member.adminConsoleContactName || '').trim();
  if (same(member.email) && (member.contactName || '').trim()) return (member.contactName || '').trim();
  return '';
};

const Icon = ({ d }: { d: React.ReactNode }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);

const LockedNavItem = ({ title, testId }: { title: string; testId: string }) => (
  <div data-testid={testId} aria-disabled="true" title="Available on CPA Standard — upgrade to create and manage perks."
    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 12, minHeight: 60, boxSizing: 'border-box',
      border: '2px solid transparent', opacity: 0.45, cursor: 'not-allowed', fontFamily: "'DM Sans', sans-serif" }}>
    <span style={{ display: 'flex', flexShrink: 0, color: GRAY }}><Icon d={<><path d="M12 5v14M5 12h14" /></>} /></span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 14, color: GRAY, fontWeight: 500 }}>{title}</span>
      <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.02em', color: GRAY }}>NOT OFFERED BY YOUR CHAMBER</span>
    </span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GRAY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto', flexShrink: 0 }} aria-hidden="true"><rect x="4" y="10.5" width="16" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 018 0v3.5" /></svg>
  </div>
);

const NavItem = ({ active, icon, title, sub, subColor, onClick, testId }: {
  active: boolean; icon: React.ReactNode; title: string; sub?: React.ReactNode; subColor?: string; onClick: () => void; testId: string;
}) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-current={active ? 'page' : undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', minHeight: 60,
      boxSizing: 'border-box', border: `2px solid ${active ? GOLD : 'transparent'}`, background: 'transparent', textAlign: 'left',
      fontFamily: "'DM Sans', sans-serif", width: '100%',
    }}>
    <span style={{ display: 'flex', flexShrink: 0, color: BLACK }}>{icon}</span>
    <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 14, color: BLACK, fontWeight: active ? 700 : 600, lineHeight: 1.3 }}>{title}</span>
      {sub ? <span style={{ fontSize: 12, fontWeight: 400, color: subColor || BLACK }}>{sub}</span> : null}
    </span>
  </button>
);

export default function CmpMemberSidebar({
  access, chamberName, memberName, memberLogo, featuredMember, sponsorLogo, sponsorLogoLink, perksOffered = true, helpContactName, helpContactEmail,
  onLogout, onNavigate,
}: CmpMemberSidebarProps) {
  const history = useHistory();
  const [helpOpen, setHelpOpen] = useState(false);
  const { pathname } = useLocation();
  const banners = useGetActiveCategoryAdvertisingBannersForChamberMemberQuery({ skip: !access.canManageBanners, fetchPolicy: 'cache-and-network' });
  const bannersActive = (banners.data?.getActiveCategoryAdvertisingBannersForChamberMember || []).length > 0;
  const go = (route: string) => { history.push(route); onNavigate?.(); };
  const isActive = (route: string) => pathname === route || pathname.startsWith(`${route}/`);
  const initials = (memberName || '').split(/\s+/).map(word => word[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  return (
    <aside data-testid="cmp-member-sidebar" style={{
      display: 'flex', flexDirection: 'column', padding: '24px 16px 20px', boxSizing: 'border-box', minHeight: '100%',
      background: '#FFFFFF', borderRight: '1px solid #ECECEE', fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 8px 14px', marginBottom: 14, borderBottom: `1px solid ${GOLD}` }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: BLACK, textAlign: 'center', letterSpacing: '0.02em' }}>
          {chamberName ? `${chamberName} Member Dashboard` : 'Member Dashboard'}
        </div>
        {memberLogo
          ? <img src={memberLogo} alt="Member logo" style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', marginTop: 2 }} />
          : <span aria-hidden="true" style={{ width: 60, height: 60, borderRadius: '50%', background: GOLD, color: '#FFFFFF', fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>{initials}</span>}
        <div style={{ fontSize: 16, fontWeight: 600, color: BLACK, textAlign: 'center' }}>{memberName}</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <span style={{ display: 'inline-flex', gap: 4, color: BLACK, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            <span>Signed in as</span><span>{access.roleLabel || 'Company Contact'}</span>
          </span>
          <span style={{ fontSize: 12, color: BLACK, textAlign: 'center', lineHeight: 1.4 }}>{getCmpMemberRoleSummary(access)}</span>
        </div>
      </div>

      <nav aria-label="Member Dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {access.canManageProfile ? (
          <NavItem testId="nav-profile" active={isActive(RedirectUri.manageCompanyProfileChamberMemberAdmin)}
            onClick={() => go(RedirectUri.manageCompanyProfileChamberMemberAdmin)} title="Manage Company Profile"
            icon={<Icon d={<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="10" r="3" /><path d="M6.2 18.3a6.5 6.5 0 0111.6 0" /></>} />}
            sub={featuredMember ? 'Featured Member' : 'Upgrade to a Featured Member and add banners to your profile page'}
            subColor={featuredMember ? GREEN : GRAY} />
        ) : null}
        {!perksOffered ? (<>
          <LockedNavItem title="Create Company Perks" testId="nav-create-perk-locked" />
          <LockedNavItem title="Manage Company Perks" testId="nav-perks-locked" />
        </>) : null}
        {perksOffered && access.canCreatePerks ? (
          <NavItem testId="nav-create-perk" active={isActive(RedirectUri.createDiscountChamberMemberAdmin)}
            onClick={() => go(RedirectUri.createDiscountChamberMemberAdmin)} title="Create Company Perks"
            icon={<Icon d={<><path d="M12 5v14M5 12h14" /></>} />}
            sub={featuredMember ? 'Featured Member' : 'Upgrade to a Featured Member and add banners to your perks you create'}
            subColor={featuredMember ? GREEN : GRAY} />
        ) : null}
        {perksOffered && access.canManagePerks ? (
          <NavItem testId="nav-perks" active={pathname === RedirectUri.manageDiscountChamberMemberAdmin}
            onClick={() => go(RedirectUri.manageDiscountChamberMemberAdmin)} title="Manage Company Perks"
            icon={<Icon d={<><path d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" /></>} />} />
        ) : null}
        {access.canManageBanners ? (
          <NavItem testId="nav-banners" active={isActive(RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember)}
            onClick={() => go(RedirectUri.chamberEngageManageCategoryAdvertisingBannersChamberMember)} title="Manage Category Advertising Banners"
            icon={<Icon d={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 15l5-5 4 4 3-3 6 6" /><circle cx="16" cy="9" r="1.5" /></>} />}
            sub={bannersActive ? 'Category Advertising Banners Active' : 'Purchase Category Advertising Banner'}
            subColor={bannersActive ? GREEN : GRAY} />
        ) : null}
        {access.canViewInvoices ? (
          <NavItem testId="nav-invoices" active={isActive(RedirectUri.memberInvoices)}
            onClick={() => go(RedirectUri.memberInvoices)} title="Invoices & Payments"
            icon={<Icon d={<><path d="M7 3h10a1.5 1.5 0 011.5 1.5V21l-2.5-1.5L13.5 21 12 19.9 10.5 21l-2.5-1.5L5.5 21V4.5A1.5 1.5 0 017 3z" /><path d="M9 8h6M9 12h6M9 16h4" /></>} />}
            sub={<MemberInvoiceNavSummary />} />
        ) : null}
        {access.canManageProfile ? (
          <NavItem testId="nav-events" active={isActive(RedirectUri.memberEvents)}
            onClick={() => go(RedirectUri.memberEvents)} title="Register for Events" sub="And update attendee details"
            icon={<Icon d={<><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4M9 15l2 2 4-4" /></>} />} />
        ) : null}
      </nav>

      <div style={{ flex: 1, minHeight: 24 }} />
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: GRAY, margin: '0 0 10px', padding: '0 8px' }}>SPONSORED BY:</div>
      <div style={{ border: '1px solid #ECECEE', borderRadius: 12, boxShadow: '0 1px 2px rgba(0,0,0,.04)', padding: 14, display: 'flex', justifyContent: 'center', margin: '0 8px' }}>
        {sponsorLogo ? (
          <a href={sponsorLogoLink || undefined} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}>
            <img src={sponsorLogo} alt="Sponsor logo" style={{ maxWidth: 200, maxHeight: 70, objectFit: 'contain' }} />
          </a>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 140, height: 48, border: '1px dashed #C4C4C8', borderRadius: 8, background: '#F7F7F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 11, color: GRAY, letterSpacing: '0.04em' }}>YOUR LOGO</span>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>Available</div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 8px 0', padding: '12px 14px', border: '1px solid #ECECEE', borderRadius: 12, background: '#FAFAFA' }}>
        <span style={{ display: 'flex', color: GOLD }}><Icon d={<><path d="M12 15l-3-3a14 14 0 0 1 7-9c1.7 0 3 1.3 3 3a14 14 0 0 1-9 7z" /><path d="M9 12H5l2-4h4M12 15v4l4-2v-4" /></>} /></span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: BLACK }}>Learning Launchpad</span>
          <span style={{ fontSize: 12, color: BLACK }}>Coming Soon</span>
        </span>
      </div>
      <div style={{ height: 1, background: '#ECECEE', margin: '24px 8px 0' }} />
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: GRAY, margin: '20px 0 8px', padding: '0 8px' }}>GENERAL</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button type="button" onClick={() => go(RedirectUri.settingsChamberMemberAdmin)} style={generalRow}>
          <img src={LanguageIcon} alt="" style={{ width: 20, height: 20 }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: BLACK }}>Language Preference</span>
        </button>
        <button type="button" onClick={() => setHelpOpen(true)} style={generalRow} data-testid="cmp-nav-help">
          <img src={HelpIcon} alt="" style={{ width: 20, height: 20 }} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: BLACK }}>Need Help?</span>
            <span style={{ fontSize: 12, color: BLACK }}>Click to send to your chamber</span>
          </span>
        </button>
        <button type="button" onClick={onLogout} style={generalRow}>
          <img src={LogoutIcon} alt="" style={{ width: 20, height: 20 }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: '#ED1C24' }}>Logout</span>
        </button>
      </div>
      <div style={{ fontSize: 11, color: GRAY, textAlign: 'center', lineHeight: 1.5, marginTop: 20 }}>
        Copyright @ {new Date().getFullYear()},<br />One Chamber System™ Dashboard
      </div>
      {helpOpen && <CmpMemberHelpModal chamberName={chamberName || 'your chamber'} defaultName={helpContactName}
        defaultEmail={helpContactEmail} onClose={() => setHelpOpen(false)} />}
    </aside>
  );
}

const generalRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 12, cursor: 'pointer',
  background: 'transparent', border: 'none', textAlign: 'left', fontFamily: "'DM Sans', sans-serif", width: '100%',
};
